type Request = { action: string; [key: string]: unknown };
type Pending = {
  resolve: (value: any) => void;
  reject: (error: Error) => void;
  dispose: () => void;
};
let worker: Worker | undefined,
  sequence = 0;
const pending = new Map<number, Pending>();
let nativeProcessor: ((request: Request) => Promise<any>) | undefined;
export function setNativeCore(processor: typeof nativeProcessor) {
  nativeProcessor = processor;
}
function instance() {
  if (!worker) {
    worker = new Worker(new URL('./core.worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = ({ data }) => {
      const request = pending.get(data.id);
      if (!request) return;
      request.dispose();
      data.ok
        ? request.resolve(data.data)
        : request.reject(new Error(data.error));
    };
    worker.onerror = () => {
      for (const request of pending.values()) {
        request.dispose();
        request.reject(
          new Error('The shared core could not start. Please reload Streamit.'),
        );
      }
      worker?.terminate();
      worker = undefined;
    };
  }
  return worker;
}
export function core<T>(request: Request, signal?: AbortSignal): Promise<T> {
  if (signal?.aborted)
    return Promise.reject(new DOMException('Cancelled', 'AbortError'));
  if (nativeProcessor)
    return nativeProcessor(request).then((value) => {
      if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
      if (!value.ok) throw new Error(value.error);
      return value.data;
    });
  if (pending.size >= 64)
    return Promise.reject(
      new Error('Too many core requests. Please try again.'),
    );
  return new Promise((resolve, reject) => {
    const id = ++sequence;
    const abort = () => {
      dispose();
      reject(new DOMException('Cancelled', 'AbortError'));
    };
    const timer = setTimeout(() => {
      dispose();
      reject(new Error('The shared core timed out.'));
    }, 15000);
    const dispose = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
      pending.delete(id);
    };
    pending.set(id, { resolve, reject, dispose });
    signal?.addEventListener('abort', abort, { once: true });
    try {
      instance().postMessage({ id, request });
    } catch (error) {
      dispose();
      reject(error);
    }
  });
}
